import { useMemo, useState } from 'react'
import type { BudgetMonth, BudgetCategory, CategoryGroup } from './types'
import { computeUnderfunded, groupTotals } from './calc'
import { formatCurrency } from './format'
import { AssignCell } from './AssignCell'
import { useAddCategory, useAddGroup, useAssignMoney, usePriorBudgetMonths } from './hooks'
import { useGoals } from '@/features/goals/hooks'
import { goalProgress } from '@/features/goals/progress'
import type { Goal } from '@/features/goals/types'
import { GoalDrawer } from './GoalDrawer'
import { ActivityDrilldown } from './ActivityDrilldown'

export type BudgetFilter = 'all' | 'underfunded' | 'overfunded' | 'available' | 'goal'

interface Props {
  month: BudgetMonth
  year: number
  monthNumber: number
  filter?: BudgetFilter
}

/**
 * YNAB-style budget grid.
 *
 * Uses `role="grid"` + `role="row"` + `role="gridcell"` ARIA so screen
 * readers + keyboard users get the spreadsheet semantics. CSS Grid
 * provides the layout; we don't use a `<table>` because the inline-
 * edit transitions are nicer with divs and we still get the same
 * a11y story via roles.
 *
 * Per WCAG 2.1 grid pattern, arrow-key nav between cells would be
 * the next ergonomic improvement — tracked as a follow-up.
 */
function passesFilter(c: BudgetCategory, filter: BudgetFilter): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'underfunded':
      // Either a goal that's behind, or no goal but available < 0.
      if (c.targetUnderfunded && c.targetUnderfunded > 0) return true
      return c.available < 0
    case 'overfunded':
      // Has a goal target and we've assigned more than the target.
      return c.target != null && c.target > 0 && c.assigned > c.target
    case 'available':
      return c.available > 0
    case 'goal':
      return c.target != null && c.target > 0
  }
}

export function BudgetTable({ month, year, monthNumber, filter = 'all' }: Props) {
  const assign = useAssignMoney()
  const addCategory = useAddCategory()
  const addGroup = useAddGroup()
  const priorMonthQueries = usePriorBudgetMonths(year, monthNumber, 3)
  const priorMonths = priorMonthQueries.map((q) => q.data ?? null)
  const goalsQuery = useGoals()
  const goalByCategory = useMemo(() => {
    const m = new Map<string, Goal>()
    for (const g of goalsQuery.data ?? []) m.set(g.categoryId, g)
    return m
  }, [goalsQuery.data])
  const [addingGroupName, setAddingGroupName] = useState(false)
  const [groupDraft, setGroupDraft] = useState('')
  const [goalDialogFor, setGoalDialogFor] = useState<BudgetCategory | null>(null)
  // Separate open flag so the category stays mounted during the
  // drawer's slide-out animation.
  const [goalOpen, setGoalOpen] = useState(false)
  const [activityFor, setActivityFor] = useState<BudgetCategory | null>(null)
  const [activityOpen, setActivityOpen] = useState(false)
  // Resolve the drawer's category against the live month doc so the
  // drawer stays fresh as assigns refetch the month.
  const goalCategory = useMemo(() => {
    if (!goalDialogFor) return null
    for (const g of month.categoryGroups) {
      const live = g.categories.find((c) => c.id === goalDialogFor.id)
      if (live) return live
    }
    return goalDialogFor
  }, [goalDialogFor, month])

  function handleAssign(category: BudgetCategory, newValue: number) {
    assign.mutate({
      year,
      month: monthNumber,
      categoryId: category.id,
      newAssigned: newValue,
    })
  }

  // Drawers push the grid aside (margin) rather than overlaying it,
  // except on small screens where the drawer is full-width anyway.
  const drawerOpen = goalOpen || activityOpen
  return (
    <section
      aria-label="Budget categories"
      className={`rounded-xl border border-zinc-200 transition-[margin] duration-200 ease-out dark:border-zinc-800 ${
        drawerOpen ? 'md:mr-[28rem]' : ''
      }`}
    >
      <GoalDrawer
        open={goalOpen}
        onClose={() => setGoalOpen(false)}
        category={goalCategory}
        priorMonths={priorMonths}
        year={year}
        monthNumber={monthNumber}
      />
      <ActivityDrilldown
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        category={activityFor}
        year={year}
        monthNumber={monthNumber}
      />
      <div
        role="grid"
        aria-label="Budget categories grid"
        aria-rowcount={month.categoryGroups.reduce((acc, g) => acc + 1 + g.categories.length, 0) + 1}
        aria-colcount={4}
        className="divide-y divide-zinc-200 dark:divide-zinc-800"
      >
        <HeaderRow />

        {month.categoryGroups
          .filter((g) => !g.isHidden)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((group) => {
            const visibleCats = group.categories.filter((c) => !c.isHidden && passesFilter(c, filter))
            // When a filter is active and a group has no matches,
            // drop the entire group from view rather than showing an
            // empty header. Keeps the chip-filtered list tight.
            if (filter !== 'all' && visibleCats.length === 0) return null
            return (
              <GroupBlock
                key={group.id}
                group={group}
                visibleCategories={visibleCats}
                goalByCategory={goalByCategory}
                year={year}
                monthNumber={monthNumber}
                onAddCategory={(name) =>
                  addCategory.mutate({ year, month: monthNumber, groupId: group.id, name })
                }
                onAssign={handleAssign}
                onOpenGoal={(c) => {
                  setGoalDialogFor(c)
                  setGoalOpen(true)
                  setActivityOpen(false)
                }}
                onOpenActivity={(c) => {
                  setActivityFor(c)
                  setActivityOpen(true)
                  setGoalOpen(false)
                }}
              />
            )
          })}

        <div role="row" className="flex items-center px-4 py-2 text-sm">
          {addingGroupName ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (groupDraft.trim()) {
                  addGroup.mutate({ year, month: monthNumber, name: groupDraft.trim() })
                  setGroupDraft('')
                  setAddingGroupName(false)
                }
              }}
              className="flex w-full items-center gap-2"
            >
              <input
                autoFocus
                value={groupDraft}
                onChange={(e) => setGroupDraft(e.target.value)}
                onBlur={() => setAddingGroupName(false)}
                placeholder="Group name"
                aria-label="New category group name"
                className="rounded-md border border-zinc-300 px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
              />
              <button type="submit" className="text-blue-600 hover:underline">
                Add group
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setAddingGroupName(true)}
              className="text-blue-600 hover:underline"
            >
              + New category group
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

function HeaderRow() {
  return (
    <div
      role="row"
      className="grid grid-cols-[1fr_120px_120px_120px] gap-4 bg-zinc-100 px-4 py-2 text-xs font-medium uppercase tracking-wide text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
    >
      <div role="columnheader">Category</div>
      <div role="columnheader" className="text-right">
        Assigned
      </div>
      <div role="columnheader" className="text-right">
        Activity
      </div>
      <div role="columnheader" className="text-right">
        Available
      </div>
    </div>
  )
}

interface GroupBlockProps {
  group: CategoryGroup
  visibleCategories: BudgetCategory[]
  goalByCategory: Map<string, Goal>
  year: number
  monthNumber: number
  onAddCategory: (name: string) => void
  onAssign: (category: BudgetCategory, newValue: number) => void
  onOpenGoal: (category: BudgetCategory) => void
  onOpenActivity: (category: BudgetCategory) => void
}

function GroupBlock({
  group,
  visibleCategories,
  goalByCategory,
  year,
  monthNumber,
  onAddCategory,
  onAssign,
  onOpenGoal,
  onOpenActivity,
}: GroupBlockProps) {
  const totals = groupTotals(group)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="bg-white dark:bg-zinc-950">
      <div
        role="row"
        onClick={() => setCollapsed((c) => !c)}
        className="grid cursor-pointer grid-cols-[1fr_120px_120px_120px] gap-4 bg-zinc-50 px-4 py-2 text-sm font-semibold hover:bg-zinc-100 dark:bg-zinc-900 dark:hover:bg-zinc-800/80"
      >
        <div role="rowheader" className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setCollapsed((c) => !c)
            }}
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${group.name}`}
            className="rounded p-0.5 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700"
          >
            <span className={`inline-block transition-transform ${collapsed ? '-rotate-90' : ''}`}>
              ▾
            </span>
          </button>
          <span>{group.name}</span>
          {collapsed && (
            <span className="text-xs font-normal text-zinc-500">
              ({visibleCategories.length})
            </span>
          )}
        </div>
        <div role="gridcell" className="text-right tabular-nums">
          {formatCurrency(totals.assigned)}
        </div>
        <div role="gridcell" className="text-right tabular-nums text-zinc-500">
          {formatCurrency(totals.activity)}
        </div>
        <div role="gridcell" className="text-right tabular-nums">
          {formatCurrency(totals.available)}
        </div>
      </div>

      {!collapsed && visibleCategories
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((category) => (
          <CategoryRow
            key={category.id}
            category={category}
            goal={goalByCategory.get(category.id) ?? null}
            year={year}
            monthNumber={monthNumber}
            onAssign={onAssign}
            onOpenGoal={() => onOpenGoal(category)}
            onOpenActivity={() => onOpenActivity(category)}
          />
        ))}

      {!collapsed && (
        <div role="row" className="px-4 py-2 text-sm">
          {adding ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (draft.trim()) {
                  onAddCategory(draft.trim())
                  setDraft('')
                  setAdding(false)
                }
              }}
              className="flex items-center gap-2"
            >
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => setAdding(false)}
                placeholder="Category name"
                aria-label={`New category in ${group.name}`}
                className="rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
              <button type="submit" className="text-blue-600 hover:underline">
                Add
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="text-zinc-500 hover:text-blue-600 hover:underline"
            >
              + Add category
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function CategoryRow({
  category,
  goal,
  year,
  monthNumber,
  onAssign,
  onOpenGoal,
  onOpenActivity,
}: {
  category: BudgetCategory
  goal: Goal | null
  year: number
  monthNumber: number
  onAssign: (c: BudgetCategory, newValue: number) => void
  onOpenGoal: () => void
  onOpenActivity: () => void
}) {
  const available = category.available
  const availableClass =
    available < 0
      ? 'text-overspent'
      : available > 0 && category.assigned > 0
        ? 'text-funded'
        : category.assigned > 0 && available === 0
          ? 'text-underfunded'
          : ''

  const overspent = available < 0 ? -available : 0
  // Cadence-aware this-month shortfall (yearly targets pro-rated over
  // months remaining); overspend (carry-in debt) is cleared on top.
  const shortfall = computeUnderfunded(category, year, monthNumber)
  const needed = Math.round(Math.max(shortfall, overspent) * 100) / 100

  const progress = goal ? goalProgress(goal, available) : null

  const hasGoal = Boolean(goal) || (category.target != null && category.target > 0)
  return (
    <div
      role="row"
      onClick={onOpenGoal}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.target === e.currentTarget) {
            e.preventDefault()
            onOpenGoal()
          }
        }
      }}
      tabIndex={0}
      aria-label={`${category.name} — ${hasGoal ? 'manage target' : 'set target'}`}
      className="group grid cursor-pointer grid-cols-[1fr_120px_120px_120px] items-center gap-4 px-4 py-1 text-sm hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 dark:hover:bg-zinc-900/60"
    >
      <div role="gridcell" className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate">{category.name}</span>
          <span
            className={`shrink-0 text-xs ${
              hasGoal ? 'text-blue-600 dark:text-blue-400' : 'text-zinc-400 opacity-0 group-hover:opacity-100'
            }`}
            aria-hidden="true"
          >
            🎯
          </span>
        </div>
        {progress && (
          <div className="mt-0.5">
            <div className="h-1 w-full overflow-hidden rounded bg-zinc-200 dark:bg-zinc-800">
              <div
                className={`h-full ${
                  progress.achieved ? 'bg-funded' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.max(0, Math.min(100, progress.percentage))}%` }}
                role="progressbar"
                aria-valuenow={Math.round(progress.percentage)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Goal progress for ${category.name}`}
              />
            </div>
            <div className="mt-0.5 text-xs text-zinc-500">{progress.description}</div>
          </div>
        )}
        {needed > 0 && (
          <div className="mt-0.5 flex items-center gap-2 text-xs">
            <span
              className={
                overspent > 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-amber-600 dark:text-amber-500'
              }
            >
              {overspent > 0 && shortfall > overspent
                ? `Overspent ${formatCurrency(overspent)} · ${formatCurrency(shortfall)} short of target`
                : overspent > 0
                  ? `Overspent ${formatCurrency(overspent)}`
                  : `Needs ${formatCurrency(needed)} more`}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onAssign(category, Math.round((category.assigned + needed) * 100) / 100)
              }}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`Fund ${category.name} with ${formatCurrency(needed)}`}
              className="rounded-full border border-blue-600 px-2 py-0.5 font-medium text-blue-600 hover:bg-blue-600 hover:text-white dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-500 dark:hover:text-white"
            >
              + Fund {formatCurrency(needed)}
            </button>
          </div>
        )}
      </div>
      <div
        role="gridcell"
        className="flex items-center justify-end gap-1"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="min-w-[80px]">
          <AssignCell
            value={category.assigned}
            onSave={(v) => onAssign(category, v)}
            ariaLabel={`Assigned for ${category.name}`}
          />
        </div>
      </div>
      <div role="gridcell" className="text-right">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onOpenActivity()
          }}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={`Show transactions behind ${formatCurrency(category.activity)} activity for ${category.name}`}
          title="Show this month's transactions"
          className="rounded px-1 py-0.5 tabular-nums text-zinc-500 underline-offset-2 hover:text-blue-600 hover:underline dark:hover:text-blue-400"
        >
          {formatCurrency(category.activity)}
        </button>
      </div>
      <div role="gridcell" className="flex justify-end">
        <AvailablePill
          assigned={category.assigned}
          available={available}
          underfunded={shortfall}
          className={availableClass}
        />
      </div>
    </div>
  )
}

/**
 * YNAB-style status pill for the Available cell. Single visual
 * encodes: overspent (red), goal behind / spent-to-zero (amber),
 * fully funded with money left (green), idle / never funded (gray).
 */
function AvailablePill({
  assigned,
  available,
  underfunded = 0,
  className,
}: {
  assigned: number
  available: number
  underfunded?: number
  className?: string
}) {
  const variant =
    available < 0
      ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
      : underfunded > 0 || (assigned > 0 && available === 0)
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
        : available > 0
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
          : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
  return (
    <span
      className={`inline-flex min-w-[80px] justify-end rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums ${variant} ${className ?? ''}`}
    >
      {formatCurrency(available)}
    </span>
  )
}
